import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useAddChild, orgErrorMessage } from '@/api/organization';

interface AddChildDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Link an existing customer to this reseller as a customer, by customer id. */
export function AddChildDialog({ open, onOpenChange }: AddChildDialogProps) {
  const { show } = useToast();
  const [customerId, setCustomerId] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setCustomerId('');
      setError(undefined);
    }
  }, [open]);

  const add = useAddChild({
    onSuccess: () => {
      show({ tone: 'success', title: 'Customer linked' });
      onOpenChange(false);
    },
    onError: (err) =>
      show({ tone: 'danger', title: orgErrorMessage(err, 'Could not link customer') }),
  });

  function handleSubmit() {
    const id = Number(customerId.trim());
    if (!customerId.trim() || !Number.isFinite(id) || id <= 0) {
      setError('Enter a valid customer id.');
      return;
    }
    setError(undefined);
    add.mutate({ customer_id: id });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Link customer</DialogTitle>
          <DialogDescription>
            Link an existing customer to your organization by their customer id.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="add-child-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Field label="Customer ID" required error={error}>
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  placeholder="e.g. 5033"
                  value={customerId}
                  invalid={!!error}
                  onChange={(e) => setCustomerId(e.target.value)}
                />
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-child-form" disabled={add.isPending}>
            {add.isPending ? 'Linking…' : 'Link customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
